// VixScript — the VixAlgoBuilder strategy VM (Phase B, v1).
//
// A small, deterministic, sandboxed scripting layer that runs INSIDE the backtest engines,
// anchored on the same "last closed signal candle" event the rule engine uses, so scripted
// strategies inherit the no-look-ahead semantics, the journal, margin/stop realism, and the
// debugger trace for free. No loops, no I/O, no randomness, hard step budgets — deterministic
// by construction, which is what keeps certification meaningful.
//
// This file is kept IDENTICAL in frontend/src/components/vixScriptEngine.js (ESM) and
// backend/vixScriptEngine.js (CJS) except for the export footer — same discipline as the
// simulateBacktest engine pair. Edit both or regenerate one from the other.
//
// Language (statements, semicolons optional):
//   let x = expr                 per-bar local
//   state.x = expr               persists across bars (numbers/booleans)
//   if (expr) { ... } else { ... }
//   fnName(args)                 host call, e.g. buy(), closeAll('long')
// Expressions: numbers, 'strings' (only as function arguments), true/false,
//   arithmetic + - * / %, comparisons == != > >= < <=, logic and/or/not (also && || !).
//
// Host API (all series anchored at shift 0 = last CLOSED signal candle):
//   open(s) high(s) low(s) close(s)             price series, s = shift back (default 0)
//   ind('id', s) / ind('id:series', s)          indicator values by operand id
//   highest('low'|'high'|..., count, s) lowest(...)
//   crossover('a','b') crossunder('a','b')      operand ids incl. price ids
//   barIndex() hour() minute() dayOfWeek()      dayOfWeek: 0=Sun..6=Sat (strategy timezone)
//   spread() point() balance() equity()
//   positionCount() hasLong() hasShort() avgEntry()
//   buy() sell()                                entry signal (opens at next bar open,
//                                               same gate as every other entry model)
//   closeAll() closeLong() closeShort()
//   setStopPoints(n) setTargetPoints(n)         re-anchor SL/TP of open positions
//   setStop(price) setTarget(price)
//   log('msg')                                  journal entry (budgeted)
//   abs(x) min(a,b) max(a,b) round(x) floor(x) ceil(x)

const VIXSCRIPT_MAX_SOURCE = 20000;
const VIXSCRIPT_MAX_TOKENS = 4000;
const VIXSCRIPT_MAX_STATEMENTS = 400;
const VIXSCRIPT_STEP_BUDGET = 8000;

const VIXSCRIPT_FUNCTIONS = new Set([
  'open', 'high', 'low', 'close', 'ind', 'highest', 'lowest', 'crossover', 'crossunder',
  'barIndex', 'hour', 'minute', 'dayOfWeek', 'spread', 'point', 'balance', 'equity',
  'positionCount', 'hasLong', 'hasShort', 'avgEntry',
  'buy', 'sell', 'closeAll', 'closeLong', 'closeShort',
  'setStop', 'setTarget', 'setStopPoints', 'setTargetPoints',
  'log', 'abs', 'min', 'max', 'round', 'floor', 'ceil',
]);

const VIXSCRIPT_KEYWORDS = new Set(['if', 'else', 'let', 'true', 'false', 'and', 'or', 'not', 'state']);

function vixScriptTokenize(source) {
  const tokens = [];
  const text = String(source || '');
  let i = 0;
  let line = 1;
  const push = (type, value) => tokens.push({ type, value, line });
  while (i < text.length) {
    const ch = text[i];
    if (ch === '\n') { line += 1; i += 1; continue; }
    if (ch === ' ' || ch === '\t' || ch === '\r' || ch === ';') { i += 1; continue; }
    if (ch === '/' && text[i + 1] === '/') { while (i < text.length && text[i] !== '\n') i += 1; continue; }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(text[i + 1] || ''))) {
      let j = i;
      while (j < text.length && /[0-9.]/.test(text[j])) j += 1;
      const raw = text.slice(i, j);
      const value = Number(raw);
      if (!Number.isFinite(value)) throw new Error(`Line ${line}: invalid number "${raw}"`);
      push('number', value);
      i = j;
      continue;
    }
    if (ch === "'" || ch === '"') {
      let j = i + 1;
      while (j < text.length && text[j] !== ch && text[j] !== '\n') j += 1;
      if (text[j] !== ch) throw new Error(`Line ${line}: unterminated string`);
      push('string', text.slice(i + 1, j));
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < text.length && /[A-Za-z0-9_]/.test(text[j])) j += 1;
      const word = text.slice(i, j);
      push(VIXSCRIPT_KEYWORDS.has(word) ? word : 'ident', word);
      i = j;
      continue;
    }
    const two = text.slice(i, i + 2);
    if (['==', '!=', '>=', '<=', '&&', '||'].includes(two)) { push('op', two); i += 2; continue; }
    if ('+-*/%<>=!(){},.'.includes(ch)) { push('op', ch); i += 1; continue; }
    throw new Error(`Line ${line}: unexpected character "${ch}"`);
  }
  if (tokens.length > VIXSCRIPT_MAX_TOKENS) throw new Error(`Script too large (${tokens.length} tokens; max ${VIXSCRIPT_MAX_TOKENS}).`);
  push('eof', null);
  return tokens;
}

function vixScriptParse(source) {
  if (String(source || '').length > VIXSCRIPT_MAX_SOURCE) {
    return { ok: false, error: `Script source exceeds ${VIXSCRIPT_MAX_SOURCE} characters.`, program: null };
  }
  let tokens;
  try {
    tokens = vixScriptTokenize(source);
  } catch (error) {
    return { ok: false, error: error.message, program: null };
  }
  let pos = 0;
  let statementCount = 0;
  // Never step past the trailing eof token — a truncated expression must produce a parse
  // error ("unexpected token eof"), not an undefined-token crash.
  const peek = () => tokens[Math.min(pos, tokens.length - 1)];
  const next = () => tokens[pos < tokens.length - 1 ? pos++ : pos];
  const fail = (message) => { throw new Error(`Line ${peek().line}: ${message}`); };
  const expectOp = (value) => { const t = next(); if (t.type !== 'op' || t.value !== value) fail(`expected "${value}"`); };

  function parsePrimary() {
    const t = next();
    if (t.type === 'number') return { k: 'num', v: t.value };
    if (t.type === 'string') return { k: 'str', v: t.value };
    if (t.type === 'true') return { k: 'num', v: 1 };
    if (t.type === 'false') return { k: 'num', v: 0 };
    if (t.type === 'op' && t.value === '(') {
      const e = parseExpr();
      expectOp(')');
      return e;
    }
    if (t.type === 'op' && t.value === '-') return { k: 'neg', e: parsePrimary() };
    if (t.type === 'op' && t.value === '!') return { k: 'not', e: parsePrimary() };
    if (t.type === 'not') return { k: 'not', e: parsePrimary() };
    if (t.type === 'state') {
      expectOp('.');
      const name = next();
      if (name.type !== 'ident') fail('expected state variable name');
      return { k: 'state', name: name.value };
    }
    if (t.type === 'ident') {
      if (peek().type === 'op' && peek().value === '(') {
        if (!VIXSCRIPT_FUNCTIONS.has(t.value)) fail(`unknown function "${t.value}"`);
        next();
        const args = [];
        if (!(peek().type === 'op' && peek().value === ')')) {
          for (;;) {
            args.push(parseExpr());
            if (peek().type === 'op' && peek().value === ',') { next(); continue; }
            break;
          }
        }
        expectOp(')');
        return { k: 'call', name: t.value, args };
      }
      return { k: 'local', name: t.value };
    }
    return fail(`unexpected token "${t.value ?? t.type}"`);
  }

  const parseMul = () => {
    let left = parsePrimary();
    while (peek().type === 'op' && ['*', '/', '%'].includes(peek().value)) {
      const op = next().value;
      left = { k: 'bin', op, l: left, r: parsePrimary() };
    }
    return left;
  };
  const parseAdd = () => {
    let left = parseMul();
    while (peek().type === 'op' && ['+', '-'].includes(peek().value)) {
      const op = next().value;
      left = { k: 'bin', op, l: left, r: parseMul() };
    }
    return left;
  };
  const parseCmp = () => {
    let left = parseAdd();
    while (peek().type === 'op' && ['>', '<', '>=', '<=', '==', '!='].includes(peek().value)) {
      const op = next().value;
      left = { k: 'bin', op, l: left, r: parseAdd() };
    }
    return left;
  };
  const parseAnd = () => {
    let left = parseCmp();
    while ((peek().type === 'and') || (peek().type === 'op' && peek().value === '&&')) {
      next();
      left = { k: 'and', l: left, r: parseCmp() };
    }
    return left;
  };
  function parseExpr() {
    let left = parseAnd();
    while ((peek().type === 'or') || (peek().type === 'op' && peek().value === '||')) {
      next();
      left = { k: 'or', l: left, r: parseAnd() };
    }
    return left;
  }

  function parseBlock() {
    if (peek().type === 'op' && peek().value === '{') {
      next();
      const body = [];
      while (!(peek().type === 'op' && peek().value === '}')) {
        if (peek().type === 'eof') fail('expected "}"');
        body.push(parseStatement());
      }
      next();
      return body;
    }
    return [parseStatement()];
  }

  function parseStatement() {
    statementCount += 1;
    if (statementCount > VIXSCRIPT_MAX_STATEMENTS) fail(`too many statements (max ${VIXSCRIPT_MAX_STATEMENTS})`);
    const t = peek();
    if (t.type === 'if') {
      next();
      expectOp('(');
      const cond = parseExpr();
      expectOp(')');
      const then = parseBlock();
      let alt = null;
      if (peek().type === 'else') {
        next();
        alt = parseBlock();
      }
      return { k: 'if', cond, then, alt };
    }
    if (t.type === 'let') {
      next();
      const name = next();
      if (name.type !== 'ident') fail('expected variable name after let');
      expectOp('=');
      return { k: 'setLocal', name: name.value, e: parseExpr() };
    }
    if (t.type === 'state') {
      next();
      expectOp('.');
      const name = next();
      if (name.type !== 'ident') fail('expected state variable name');
      expectOp('=');
      return { k: 'setState', name: name.value, e: parseExpr() };
    }
    if (t.type === 'ident') {
      const expr = parseExpr();
      if (expr.k !== 'call') fail('only function calls can stand alone as statements');
      return { k: 'expr', e: expr };
    }
    return fail(`unexpected token "${t.value ?? t.type}"`);
  }

  try {
    const body = [];
    while (peek().type !== 'eof') body.push(parseStatement());
    return { ok: true, error: '', program: { body }, statementCount };
  } catch (error) {
    return { ok: false, error: error.message, program: null };
  }
}

// Compile once per run; returns { ok, error, program }.
function compileVixScript(source) {
  return vixScriptParse(source);
}

// Execute one signal bar. `host` maps function names to implementations; `state` persists
// across bars. Throws only through the returned { error } — a runtime error disables the
// script for the rest of the run (the engine journals it once).
function runVixScriptBar(compiled, host, state) {
  let steps = 0;
  const locals = Object.create(null);
  const toNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : (v === true ? 1 : 0));
  const truthy = (v) => toNumber(v) !== 0;

  function evalExpr(node) {
    steps += 1;
    if (steps > VIXSCRIPT_STEP_BUDGET) throw new Error(`step budget exceeded (${VIXSCRIPT_STEP_BUDGET} per bar)`);
    switch (node.k) {
      case 'num': return node.v;
      case 'str': return node.v;
      case 'neg': return -toNumber(evalExpr(node.e));
      case 'not': return truthy(evalExpr(node.e)) ? 0 : 1;
      case 'local': {
        if (!(node.name in locals)) throw new Error(`unknown variable "${node.name}"`);
        return locals[node.name];
      }
      case 'state': return toNumber(state[node.name] ?? 0);
      case 'and': return truthy(evalExpr(node.l)) && truthy(evalExpr(node.r)) ? 1 : 0;
      case 'or': return truthy(evalExpr(node.l)) || truthy(evalExpr(node.r)) ? 1 : 0;
      case 'bin': {
        const l = evalExpr(node.l);
        const r = evalExpr(node.r);
        const ln = toNumber(l);
        const rn = toNumber(r);
        switch (node.op) {
          case '+': return ln + rn;
          case '-': return ln - rn;
          case '*': return ln * rn;
          case '/': return rn === 0 ? 0 : ln / rn;
          case '%': return rn === 0 ? 0 : ln % rn;
          case '>': return ln > rn ? 1 : 0;
          case '<': return ln < rn ? 1 : 0;
          case '>=': return ln >= rn ? 1 : 0;
          case '<=': return ln <= rn ? 1 : 0;
          case '==': return ln === rn ? 1 : 0;
          case '!=': return ln !== rn ? 1 : 0;
          default: throw new Error(`unknown operator "${node.op}"`);
        }
      }
      case 'call': {
        const fn = host[node.name];
        if (typeof fn !== 'function') throw new Error(`function "${node.name}" is not available`);
        return fn(...node.args.map(evalExpr));
      }
      default: throw new Error(`unknown expression node "${node.k}"`);
    }
  }

  function execBlock(body) {
    for (const statement of body) {
      steps += 1;
      if (steps > VIXSCRIPT_STEP_BUDGET) throw new Error(`step budget exceeded (${VIXSCRIPT_STEP_BUDGET} per bar)`);
      switch (statement.k) {
        case 'if':
          if (truthy(evalExpr(statement.cond))) execBlock(statement.then);
          else if (statement.alt) execBlock(statement.alt);
          break;
        case 'setLocal':
          locals[statement.name] = evalExpr(statement.e);
          break;
        case 'setState': {
          const value = evalExpr(statement.e);
          state[statement.name] = toNumber(value);
          break;
        }
        case 'expr':
          evalExpr(statement.e);
          break;
        default: throw new Error(`unknown statement "${statement.k}"`);
      }
    }
  }

  try {
    execBlock(compiled.program.body);
    return { error: '' };
  } catch (error) {
    return { error: error.message };
  }
}

/* EXPORT_FOOTER */
export {
  compileVixScript,
  runVixScriptBar,
  VIXSCRIPT_FUNCTIONS,
  VIXSCRIPT_MAX_SOURCE,
};
